import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { CustomerPhoneType } from "@autorepair/contracts";
import { ApiError, customersApi } from "../lib/api";
import { formatPhoneAsTyped } from "../lib/phone";
import { applyDigitPreservingTransform } from "../lib/inputCursor";
import { useConfirmGate } from "../lib/useConfirm";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernSelect,
} from "../components/ui-modern";

/**
 * The modern theme's New Customer page — see docs/FRONTEND_BACKLOG.md
 * Round 62 (the 4-small-pages round following Round 61's "all 11 mockup
 * screens" milestone; these 4 sit outside that original set).
 *
 * `CustomerForm.tsx` (shared by this page and EditCustomerPage.tsx) is
 * styled entirely off `ui.tsx`'s classic primitives (Card/Field/Input) and
 * is out of scope to touch this round (used unmodified by
 * NewCustomerPageClassic.tsx below) — so this inlines the same field set,
 * validation, and duplicate-customer check itself, restyled with
 * ui-modern.tsx's primitives, same tradeoff every other Modern screen makes
 * (thin re-run of the same calls/logic, not a shared hook/component).
 *
 * One deliberate difference vs. CustomerForm's own Input: fields here pass
 * ModernInput's own opt-in `uppercase` prop explicitly (name — never phone
 * or email) rather than uppercasing unconditionally the way `ui.tsx`'s
 * classic Input always does — see ModernInput's own comment for why
 * that's opt-in in the modern theme.
 *
 * Customer addresses are not collected anywhere (GDPR data minimization,
 * tmp/Notes.txt): a customer is just name, email and phone. The old address
 * columns are pending a drop — see docs/DB_DELAYED_ACTIONS.md.
 */
export function NewCustomerPageModern() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const fromWorkOrder = returnTo === "work-order";
  const initialDisplayName = searchParams.get("name") ?? undefined;

  const [displayName, setDisplayName] = useState(initialDisplayName ?? "");
  const [phoneType, setPhoneType] = useState<CustomerPhoneType>("mobile");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const confirmGate = useConfirmGate();

  // Same "did you mean an existing customer?" hint as CustomerForm.tsx —
  // light debounce, never blocks submission, just links to what it found.
  const [debouncedName, setDebouncedName] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(displayName.trim()), 300);
    return () => clearTimeout(timer);
  }, [displayName]);
  const duplicateCheck = useQuery({
    queryKey: ["customers", "dup-check", debouncedName],
    queryFn: () => customersApi.list({ search: debouncedName, limit: 5 }),
    enabled: debouncedName.length >= 2,
  });
  const possibleDuplicates = duplicateCheck.data?.items ?? [];

  async function save(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const { customer } = await customersApi.create({
        displayName,
        email: email.trim() || null,
        taxExempt: false,
        phones: phoneNumber.trim() ? [{ phoneType, phoneNumber: phoneNumber.trim(), isPrimary: true, sortOrder: 0 }] : [],
      });
      navigate(fromWorkOrder ? `/work-orders/new?customerId=${customer.id}` : `/customers/${customer.customerNumber}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save this customer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs
        items={
          fromWorkOrder
            ? [{ label: "New Work Order", to: "/work-orders/new" }, { label: "Add Customer" }]
            : [{ label: "Customers", to: "/customers" }, { label: "Add Customer" }]
        }
      />
      <h1 className="m-0 mb-4 text-[27px] font-bold text-[#071a33]">New Customer</h1>

      <ModernCard>
        {error && <ModernErrorBanner message={error} />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmGate.request(() => void save());
          }}
        >
          <ModernField label="Customer's Name" htmlFor="displayName" required>
            <ModernInput id="displayName" required autoFocus uppercase value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">How they&apos;d like to be addressed, e.g. their full name.</p>
            {possibleDuplicates.length > 0 && (
              <div className="mt-2 rounded-[10px] border border-dashed border-[#e2e7ee] px-3.5 py-3">
                <p className="mb-2 text-[13px] text-[#4a5361]">Already a customer? Check before adding a duplicate:</p>
                <div className="flex flex-col gap-2">
                  {possibleDuplicates.map((c) => (
                    <Link
                      key={c.id}
                      to={`/customers/${c.customerNumber}`}
                      className="rounded-[10px] border border-[#e2e7ee] px-3.5 py-2.5 text-[13.5px] text-[#101b2d] no-underline hover:border-[#0f6d86]"
                    >
                      {c.displayName}{" "}
                      <span className="text-[#8b97a8]">
                        · Cust #{c.customerNumber}
                        {c.phones[0] ? ` · ${c.phones[0].phoneNumber}` : ""}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </ModernField>

          <ModernField label="Phone Type" htmlFor="phoneType">
            <ModernSelect id="phoneType" value={phoneType} onChange={(e) => setPhoneType(e.target.value as CustomerPhoneType)}>
              <option value="mobile">Mobile</option>
              <option value="home">Home</option>
              <option value="work">Work</option>
            </ModernSelect>
          </ModernField>
          <ModernField label="Phone Number" htmlFor="phoneNumber">
            <ModernInput
              id="phoneNumber"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(applyDigitPreservingTransform(e.target, formatPhoneAsTyped))}
            />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Optional, but makes them much easier to find later.</p>
          </ModernField>

          <ModernField label="Email" htmlFor="email">
            <ModernInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">
              Optional. To email estimates and invoices to this customer directly from Bay2Bill, enter a valid email address they check.
            </p>
          </ModernField>

          <div className="mt-5 flex gap-2.5">
            <ModernButton type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save Customer"}
            </ModernButton>
            <ModernButton type="button" variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </ModernButton>
          </div>
        </form>
      </ModernCard>

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Save this new customer?" />
    </div>
  );
}
